import { supabase } from "./supabase";

export const compiledInternalProgramRoutes = {
  home: "/",
  files: "/arquivos",
  trash: "/lixeira",
  users: "/usuarios",
  activity: "/atividade",
  status: "/status",
  settings: "/configuracoes",
  "host-files": "/projetos",
  "host-terminal": "/terminal",
  "host-codex": "/codex",
  "host-browser": "/navegador",
  crm: "/crm",
  workbench: "/central-projetos",
  "remote-desktop": "/servidor-ubuntu",
  operations: "/operacoes",
} as const;

export type CompiledProgramSlug = keyof typeof compiledInternalProgramRoutes;
export type ProgramKind = "internal" | "remote";
export type ProgramStatus = "planned" | "enabled" | "disabled";
export const programCatalogChangedEvent = "base-sul-os:program-catalog-changed";

export type ProgramCatalogEntry = {
  id: string;
  slug: string;
  title: string;
  description: string;
  kind: ProgramKind;
  status: ProgramStatus;
  route: string | null;
  iconKey: string;
  catalogOrder: number;
  pinned: boolean;
  sortOrder: number;
};

type ProgramRow = {
  id: unknown;
  slug: unknown;
  title: unknown;
  description: unknown;
  kind: unknown;
  status: unknown;
  route: unknown;
  icon_key: unknown;
  catalog_order: unknown;
  pinned: unknown;
  sort_order: unknown;
};

export class ProgramsClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProgramsClientError";
  }
}

export async function listMyPrograms(): Promise<ProgramCatalogEntry[]> {
  const client = getClient();
  const { data, error } = await client.rpc("list_my_programs");
  if (error) throw safeError(error, "Não foi possível carregar o catálogo de programas.");
  if (!Array.isArray(data)) throw new ProgramsClientError("O servidor retornou um catálogo inválido.");

  const entries = data.map((row) => parseProgram(row as ProgramRow));
  return entries.sort((left, right) =>
    Number(right.pinned) - Number(left.pinned)
    || left.sortOrder - right.sortOrder
    || left.catalogOrder - right.catalogOrder
    || left.title.localeCompare(right.title, "pt-BR"));
}

export async function setMyProgramPreference(
  programId: string,
  pinned: boolean,
  sortOrder: number,
): Promise<void> {
  const client = getClient();
  if (!isUuid(programId) || !Number.isSafeInteger(sortOrder) || sortOrder < 0 || sortOrder > 10_000) {
    throw new ProgramsClientError("A preferência informada é inválida.");
  }
  const { error } = await client.rpc("set_my_program_preference", {
    p_program_id: programId,
    p_pinned: Boolean(pinned),
    p_sort_order: sortOrder,
  });
  if (error) throw safeError(error, "Não foi possível salvar a preferência do programa.");
}

export async function reorderMyPrograms(programIds: string[]): Promise<void> {
  const client = getClient();
  if (
    programIds.length === 0
    || programIds.length > 100
    || new Set(programIds).size !== programIds.length
    || programIds.some((programId) => !isUuid(programId))
  ) {
    throw new ProgramsClientError("A ordem informada é inválida.");
  }
  const { error } = await client.rpc("reorder_my_programs", { p_program_ids: programIds });
  if (error) throw safeError(error, "Não foi possível salvar a ordem dos programas.");
}

export function notifyProgramCatalogChanged(): void {
  window.dispatchEvent(new Event(programCatalogChangedEvent));
}

export function compiledRouteForProgram(program: ProgramCatalogEntry): string | null {
  if (program.kind !== "internal" || program.status !== "enabled") return null;
  if (!Object.hasOwn(compiledInternalProgramRoutes, program.slug)) return null;
  const route = compiledInternalProgramRoutes[program.slug as CompiledProgramSlug];
  return program.route === route ? route : null;
}

function parseProgram(row: ProgramRow): ProgramCatalogEntry {
  if (
    !isUuid(row.id)
    || !isShortText(row.slug, 64)
    || !isShortText(row.title, 120)
    || !isShortText(row.description, 240)
    || !isShortText(row.icon_key, 64)
    || !isKind(row.kind)
    || !isStatus(row.status)
    || !(row.route === null || (typeof row.route === "string" && row.route.startsWith("/")))
    || !isCatalogNumber(row.catalog_order)
    || typeof row.pinned !== "boolean"
    || !isCatalogNumber(row.sort_order)
    || (row.kind === "remote" && row.route !== null)
  ) {
    throw new ProgramsClientError("O servidor retornou um catálogo inválido.");
  }

  return {
    id: row.id,
    slug: row.slug,
    title: row.slug === "home" ? "Meu servidor" : row.title,
    description: row.description,
    kind: row.kind,
    status: row.status,
    route: row.route,
    iconKey: row.icon_key,
    catalogOrder: row.catalog_order,
    pinned: row.pinned,
    sortOrder: row.sort_order,
  };
}

function getClient() {
  if (!supabase) throw new ProgramsClientError("Backend não configurado.");
  return supabase;
}

function safeError(error: unknown, fallback: string) {
  const code = error && typeof error === "object" && "code" in error ? String(error.code) : "";
  const messages: Record<string, string> = {
    "22023": "A preferência informada é inválida.",
    "42501": "Você não tem permissão para acessar este programa.",
    PGRST301: "Sua sessão expirou. Entre novamente.",
  };
  return new ProgramsClientError(messages[code] ?? fallback);
}

function isUuid(value: unknown): value is string {
  return typeof value === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function isShortText(value: unknown, maximum: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= maximum;
}

function isCatalogNumber(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= 10_000;
}

function isKind(value: unknown): value is ProgramKind {
  return value === "internal" || value === "remote";
}

function isStatus(value: unknown): value is ProgramStatus {
  return value === "planned" || value === "enabled" || value === "disabled";
}
